import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useUnassignFoundingBulk } from "./use-founding-mutations";
import type { BulkFoundingResult } from "./types";

interface UnassignFoundingDialogProps {
  readonly propertyIds: readonly string[];
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onDone: () => void;
}

/**
 * Confirmation dialog for bulk-removing founding discounts. Unassign is a
 * reversible ops action (ADR-0010 §5) but still billing-affecting, so — like
 * every other mutating action in the BO — it's gated behind a confirmation
 * step, surfaces server errors, and reports per-property partial failures
 * instead of clearing the selection blindly.
 */
export function UnassignFoundingDialog({
  propertyIds,
  open,
  onOpenChange,
  onDone,
}: UnassignFoundingDialogProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkFoundingResult | null>(null);
  const unassign = useUnassignFoundingBulk();

  useEffect(() => {
    if (open) {
      setError(null);
      setResult(null);
    }
  }, [open]);

  const handleClose = (next: boolean) => {
    if (!next && unassign.isPending) return;
    onOpenChange(next);
  };

  const handleSubmit = () => {
    setError(null);
    unassign.mutate([...propertyIds], {
      onSuccess: (res) => {
        setResult(res);
        onDone();
      },
      onError: (err) => {
        setError(
          resolveMutationErrorMessage(err, t, {
            forbiddenKey: "founding.errors.forbidden",
            serverPrefixKey: "founding.errors.serverPrefix",
            genericKey: "founding.errors.generic",
          }),
        );
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="unassign-founding-dialog">
        <DialogHeader>
          <DialogTitle>{t("founding.unassign.title")}</DialogTitle>
          <DialogDescription>
            {t("founding.unassign.description", { count: propertyIds.length })}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-2 py-4" data-testid="unassign-founding-result">
            <p className="font-sans text-sm">
              {t("founding.unassign.result", {
                succeeded: result.succeeded.length,
                failed: result.failed.length,
              })}
            </p>
            {result.failed.length > 0 && (
              <ul className="max-h-40 space-y-1 overflow-y-auto font-sans text-xs text-muted-foreground">
                {result.failed.map((f) => (
                  <li key={f.propertyId}>
                    {f.propertyId}: {f.errorCode}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          error && (
            <p
              data-testid="unassign-founding-error"
              className="py-2 font-sans text-sm text-destructive"
            >
              {error}
            </p>
          )
        )}

        <DialogFooter>
          {result ? (
            <Button
              type="button"
              data-testid="unassign-founding-close"
              onClick={() => onOpenChange(false)}
            >
              {t("founding.unassign.close")}
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={unassign.isPending}
              >
                {t("founding.unassign.cancel")}
              </Button>
              <Button
                type="button"
                variant="destructive"
                data-testid="unassign-founding-submit"
                onClick={handleSubmit}
                disabled={propertyIds.length === 0 || unassign.isPending}
              >
                {t("founding.unassign.confirm")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
